import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
    MapPin, Phone, Mail, Clock, Search, RefreshCw, 
    AlertCircle, Building2, User, Copy, Check, Smartphone, MessageCircle
} from 'lucide-react';
import { centroServicioService } from '../../services/centroServicioService';
import { useNavigate } from 'react-router-dom';

const CentrosServicios = () => {
    const [centros, setCentros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const navigate = useNavigate();
     const initialLoad = useRef(false);


    const cargarCentros = async () => {
        setLoading(true);
        setError('');
        const result = await centroServicioService.obtenerCentros();
        if (result.success) {
            setCentros(result.data);
        } else {
            setError(result.error);
            if (result.code === 401 || result.code === 403) setTimeout(() => navigate('/login'), 2000);
        }
        setLoading(false);
    };

   useEffect(() => {
    if (initialLoad.current) return;
    initialLoad.current = true;
    cargarCentros();
}, []);

    const centrosFiltrados = useMemo(() => {
        if (!searchTerm) return centros;
        const term = searchTerm.toLowerCase();
        return centros.filter(c => 
            c.nombre?.toLowerCase().includes(term) ||
            c.distrito?.toLowerCase().includes(term) ||
            c.codigo?.toLowerCase().includes(term)
        );
    }, [centros, searchTerm]);

    // Renderizado elegante de teléfonos
    const renderPhones = (phonesStr, type) => {
        if (!phonesStr) return null;
        return phonesStr.split(',').map((phone, idx) => {
            const cleanPhone = phone.trim().replace(/\D/g, '');
            if (!cleanPhone) return null;
            const isMobile = cleanPhone.length === 9 && cleanPhone.startsWith('9');
            
            return (
                <div key={idx} className="flex items-center gap-2 group">
                    <a 
                        href={`tel:+51${cleanPhone}`} 
                        className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 transition-all hover:translate-x-0.5"
                    >
                        <Phone className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500" />
                        {phone.trim()}
                    </a>
                    {isMobile && (
                        <a 
                            href={`https://wa.me/51${cleanPhone}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="p-1 rounded-md bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 transition-all hover:scale-110"
                            title="WhatsApp"
                        >
                            <MessageCircle className="w-3.5 h-3.5" />
                        </a>
                    )}
                </div>
            );
        });
    };

    const handleCopy = (centro) => {
        const text = `${centro.nombre}\n📍 ${centro.direccion}, ${centro.distrito}\n ${centro.telefono || centro.celular}\n⏰ ${centro.horario}`;
        navigator.clipboard.writeText(text);
        setCopiedId(centro.id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    return (
        <div className="p-4 lg:p-6 w-full max-w-[1600px] mx-auto">
            {/* Encabezado */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-500/20">
                            <Building2 className="w-6 h-6 text-white" />
                        </div>
                        Centros de Servicio
                    </h1>
                    <p className="text-gray-500 text-sm mt-1 ml-11">Directorio de atención y soporte técnico DXN</p>
                </div>
                
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-none">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input 
                            type="text" 
                            placeholder="Buscar centro, distrito o código..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full sm:w-72 pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all shadow-sm"
                        />
                    </div>
                    <button 
                        onClick={cargarCentros} 
                        disabled={loading} 
                        className="p-2.5 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse space-y-3">
                            <div className="flex gap-2">
                                <div className="h-5 bg-gray-100 rounded w-16"></div>
                                <div className="h-5 bg-gray-100 rounded w-20"></div>
                            </div>
                            <div className="h-4 bg-gray-100 rounded w-3/4"></div>
                            <div className="space-y-2">
                                <div className="h-3 bg-gray-100 rounded w-full"></div>
                                <div className="h-3 bg-gray-100 rounded w-5/6"></div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : centrosFiltrados.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
                    <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm font-medium">No se encontraron centros de servicio</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {centrosFiltrados.map((centro) => (
                        <motion.div
                            key={centro.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            whileHover={{ y: -2 }}
                            className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-300 overflow-hidden group"
                        >
                            {/* Header con gradiente sutil */}
                            <div className="relative p-4 bg-gradient-to-br from-blue-50 via-white to-purple-50 border-b border-gray-100">
                                <div className="flex justify-between items-start gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="px-2 py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[10px] font-bold rounded-md shadow-sm">
                                                Nro. {centro.nro}
                                            </span>
                                            <span className="text-[10px] font-mono text-gray-600 bg-white px-2 py-1 rounded-md border border-gray-200 shadow-sm">
                                                {centro.codigo}
                                            </span>
                                        </div>
                                        <h3 className="font-bold text-gray-900 text-sm leading-tight line-clamp-2" title={centro.nombre}>
                                            {centro.nombre}
                                        </h3>
                                        {centro.responsable && (
                                            <p className="text-[11px] text-gray-600 mt-1.5 flex items-center gap-1.5 bg-gray-100/50 w-fit px-2 py-0.5 rounded-md">
                                                <User className="w-3 h-3 text-gray-500" /> 
                                                <span className="font-medium">{centro.responsable}</span>
                                            </p>
                                        )}
                                    </div>
                                    <button 
                                        onClick={() => handleCopy(centro)} 
                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                        title="Copiar datos"
                                    >
                                        {copiedId === centro.id ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {/* Cuerpo */}
                            <div className="p-4 space-y-3">
                                {/* Ubicación */}
                                <div className="flex items-start gap-3">
                                    <div className="p-1.5 bg-blue-50 rounded-lg shrink-0">
                                        <MapPin className="w-3.5 h-3.5 text-blue-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold text-gray-800 mb-0.5">{centro.distrito || 'Sin distrito'}</p>
                                        <p className="text-xs text-gray-600 leading-snug">{centro.direccion || 'Sin dirección'}</p>
                                        {centro.referencia && (
                                            <p className="text-[10px] text-gray-500 mt-1 italic flex items-center gap-1">
                                                <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                                                {centro.referencia}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Teléfonos */}
                                {(centro.telefono || centro.celular) && (
                                    <div className="flex items-start gap-3">
                                        <div className="p-1.5 bg-green-50 rounded-lg shrink-0">
                                            <Phone className="w-3.5 h-3.5 text-green-600" />
                                        </div>
                                        <div className="flex-1 min-w-0 space-y-1.5">
                                            {centro.telefono && renderPhones(centro.telefono, 'fijo')}
                                            {centro.celular && renderPhones(centro.celular, 'celular')}
                                        </div>
                                    </div>
                                )}

                                {/* Email */}
                                {centro.email && (
                                    <div className="flex items-start gap-3">
                                        <div className="p-1.5 bg-purple-50 rounded-lg shrink-0">
                                            <Mail className="w-3.5 h-3.5 text-purple-600" />
                                        </div>
                                        <div className="flex-1 min-w-0 space-y-1">
                                            {centro.email.split(',').map((mail, idx) => (
                                                <a 
                                                    key={idx} 
                                                    href={`mailto:${mail.trim()}`} 
                                                    className="block text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors truncate"
                                                >
                                                    {mail.trim()}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Horario */}
                                {centro.horario && (
                                    <div className="flex items-start gap-3">
                                        <div className="p-1.5 bg-orange-50 rounded-lg shrink-0">
                                            <Clock className="w-3.5 h-3.5 text-orange-600" />
                                        </div>
                                        <p className="text-xs text-gray-700 leading-snug font-medium">{centro.horario}</p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CentrosServicios;